/* eslint-disable @typescript-eslint/no-unused-vars */

import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useFormik } from 'formik';
import { useNavigate } from 'react-router-dom';
import PageWrapper from '../../layout/PageWrapper/PageWrapper';
import Button from '../../components/bootstrap/Button';
import FormGroup from '../../components/bootstrap/forms/FormGroup';
import Card, { CardBody, CardLabel, CardTitle } from '../../components/bootstrap/Card';
import Select from '../../components/bootstrap/forms/Select';
import Input from '../../components/bootstrap/forms/Input';
import { SubHeaderLeft } from '../../layout/SubHeader/SubHeader';
import SubHeader from '../../layout/SubHeader/SubHeader';
import Page from '../../layout/Page/Page';
import { selectLoginResponse } from '../../redux/login/login.selector';
import { getMedicalAllowanceReportStart, updateMedicalAllowanceStatusStart } from '../../redux/NewReport/NewReport.actions';
import { selectMedicalAllowanceReportResponse, selectUpdateMedicalAllowanceStatusResponse } from '../../redux/NewReport/NewReport.selectors';
import TableSpinner from '../../common/table/TableSpinner';
import dayjs from 'dayjs';
import Search from '../../components/Search';
import Icon from '../../components/icon/Icon';
import Swal from 'sweetalert2/dist/sweetalert2.js';
import 'sweetalert2/dist/sweetalert2.css';

const MedicalAllowanceReportPage = () => {
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const loginData = useSelector(selectLoginResponse);
	const reportsResponse = useSelector(selectMedicalAllowanceReportResponse);
	const updateStatusResponse = useSelector(selectUpdateMedicalAllowanceStatusResponse);
	const [isLoading, setIsLoading] = useState(false);
	const [reportData, setReportData] = useState<any[]>([]);
	const [filteredData, setFilteredData] = useState<any[]>([]);

	const [statusOptions] = useState<string[]>([
		'all',
		'Approved',
		'Pending',
		'Rejected',
	]);
	const [selectedStatus, setSelectedStatus] = useState('all');
	const [employeeNameFilter, setEmployeeNameFilter] = useState<string>('');
	const [employeeCodeFilter, setEmployeeCodeFilter] = useState<string>('');

	// Handle update status response
	useEffect(() => {
		if (updateStatusResponse) {
			if (updateStatusResponse.status === 'success') {
				Swal.fire({
					title: 'Success!',
					text: updateStatusResponse.message_text || 'Medical allowance status updated successfully',
					icon: 'success',
					confirmButtonText: 'OK'
				}).then(() => {
					// Refresh the report
					formik.handleSubmit();
				});
			} else if (updateStatusResponse.status === 'error') {
				Swal.fire({
					title: 'Error!',
					text: updateStatusResponse.message_text || 'Failed to update medical allowance status',
					icon: 'error',
					confirmButtonText: 'OK'
				});
			}
		}
	}, [updateStatusResponse]);

	const getStatusColor = (status: string) => {
		switch (status?.toLowerCase()) {
			case 'approved':
				return 'bg-success-subtle';
			case 'pending':
				return 'bg-warning-subtle';
			case 'rejected':
				return 'bg-danger-subtle';
			default:
				return '';
		}
	};

	// Filter data when status, employee name, or employee code changes
	useEffect(() => {
		if (reportData.length > 0) {
			let filtered = reportData;

			if (process.env.NODE_ENV === 'development') console.log('Filtering - Total records:', reportData.length);
			if (process.env.NODE_ENV === 'development') console.log('Selected Status:', selectedStatus);

			// Filter by status
			if (selectedStatus !== 'all') {
				filtered = filtered.filter((row) => {
					const status = row.Status || row.status;
					return status?.toLowerCase() === selectedStatus.toLowerCase();
				});
				if (process.env.NODE_ENV === 'development') console.log('After status filter:', filtered.length);
			}

			// Filter by employee name
			if (employeeNameFilter) {
				filtered = filtered.filter((row) => {
					const employeeName = row.Employee_Name || row.employeeName || '';
					return employeeName.toLowerCase().includes(employeeNameFilter.toLowerCase());
				});
				if (process.env.NODE_ENV === 'development') console.log('After employee name filter:', filtered.length);
			}

			// Filter by employee code
			if (employeeCodeFilter) {
				filtered = filtered.filter((row) => {
					const employeeCode = row.Employee_Code || row.employeeCode || '';
					return employeeCode.toLowerCase().includes(employeeCodeFilter.toLowerCase());
				});
				if (process.env.NODE_ENV === 'development') console.log('After employee code filter:', filtered.length);
			}

			if (process.env.NODE_ENV === 'development') console.log('Final filtered records:', filtered.length);
			setFilteredData(filtered);
		} else {
			setFilteredData([]);
		}
	}, [
		selectedStatus,
		employeeNameFilter,
		employeeCodeFilter,
		reportData,
	]);

	const handleStatusChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
		setSelectedStatus(e.target.value);
	};

	// Add custom styles for the table
	const tableStyles = {
		'--bs-table-bg': 'transparent',
		'--bs-table-striped-bg': 'transparent',
		'--bs-table-hover-bg': 'rgba(0, 0, 0, 0.075)',
		'--bs-table-border-color': '#dee2e6',
		'--bs-table-striped-color': '#212529',
		'--bs-table-active-bg': 'rgba(0, 0, 0, 0.1)',
		'--bs-table-active-color': '#212529',
		'--bs-table-hover-color': '#212529',
	} as React.CSSProperties;

	const formik = useFormik({
		initialValues: {
			startDate: dayjs().startOf('month').format('YYYY-MM-DD'),
			endDate: dayjs().endOf('month').format('YYYY-MM-DD'),
		},
		onSubmit: (values) => {
			setIsLoading(true);
			const postData = {
				startDate: values.startDate,
				endDate: values.endDate,
				organisationID: loginData?.data?.organisationID || loginData?.organisationID || 1,
			};
			dispatch(getMedicalAllowanceReportStart(postData));
		},
	});

	useEffect(() => {
		if (reportsResponse) {
			setIsLoading(false);
			if (reportsResponse.status === 'success' && reportsResponse.data) {
				const responseData = Array.isArray(reportsResponse.data)
					? reportsResponse.data
					: [reportsResponse.data];

				if (process.env.NODE_ENV === 'development') console.log('Medical Allowance Report - Raw Data:', responseData);
				if (process.env.NODE_ENV === 'development') console.log('Total Records Received:', responseData.length);
				
				// Check for duplicates by medicalAllowanceID
				const allowanceIDs = responseData.map((r: any) => r.medicalAllowanceID);
				const duplicateIDs = allowanceIDs.filter((id: any, index: number) => allowanceIDs.indexOf(id) !== index);
				if (duplicateIDs.length > 0) {
					if (process.env.NODE_ENV === 'development') console.warn('DUPLICATE medicalAllowanceIDs found:', duplicateIDs);
					if (process.env.NODE_ENV === 'development') console.warn('Duplicate records:', responseData.filter((r: any) => duplicateIDs.includes(r.medicalAllowanceID)));
				}

				setReportData(responseData);
				setFilteredData(responseData);
			} else {
				// Handle error response
				if (process.env.NODE_ENV === 'development') console.error('Error in reports response:', reportsResponse);
				setReportData([]);
				setFilteredData([]);
			}
		}
	}, [reportsResponse]);

	const handleExport = () => {
		try {
			if (!filteredData || filteredData.length === 0) {
				if (process.env.NODE_ENV === 'development') console.error('No data available to export');
				return;
			}

			// Convert filtered data to CSV format
			const headers = Object.keys(filteredData[0]);
			const csvContent = [
				headers.join(','),
				...filteredData.map((row) =>
					headers
						.map((header) => {
							let value = row[header];
							if (value === null || value === undefined) {
								value = '';
							}
							value = String(value);
							value = value.replace(/"/g, '""');
							if (
								value.includes(',') ||
								value.includes('\n') ||
								value.includes('"')
							) {
								value = `"${value}"`;
							}
							return value;
						})
						.join(','),
				),
			].join('\n');

			const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
			const url = window.URL.createObjectURL(blob);
			const a = document.createElement('a');
			a.href = url;
			const statusText = selectedStatus === 'all' ? 'All_Statuses' : selectedStatus;
			a.download = `Medical_Allowance_Report_${statusText}_${formik.values.startDate}_to_${formik.values.endDate}.csv`;
			document.body.appendChild(a);
			a.click();
			window.URL.revokeObjectURL(url);
			document.body.removeChild(a);
		} catch (error) {
			if (process.env.NODE_ENV === 'development') console.error('Error exporting report:', error);
		}
	};

	const renderTableHeaders = () => {
		if (filteredData.length === 0) return null;
		const headers: JSX.Element[] = [];

		// Add serial number header
		headers.push(
			<th key='serial' className='bg-light'>
				S.No
			</th>,
		);

		Object.keys(filteredData[0]).forEach((key) => {
			// Skip medicalAllowanceID from headers
			if (key === 'medicalAllowanceID') return;
			
			// Add filter inputs for specific columns
			if (key === 'Employee_Name' || key === 'employeeName') {
				headers.push(
					<th key={key} className='bg-light'>
						<div className='d-flex flex-column'>
							<span className='fw-bold mb-1'>Employee Name</span>
							<Input
								type='text'
								placeholder='Filter...'
								size='sm'
								className='form-control-sm'
								onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
									setEmployeeNameFilter(e.target.value)
								}
								value={employeeNameFilter}
							/>
						</div>
					</th>,
				);
			} else if (key === 'Employee_Code' || key === 'employeeCode') {
				headers.push(
					<th key={key} className='bg-light'>
						<div className='d-flex flex-column'>
							<span className='fw-bold mb-1'>Employee Code</span>
							<Input
								type='text'
								placeholder='Filter...'
								size='sm'
								className='form-control-sm'
								onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
									setEmployeeCodeFilter(e.target.value)
								}
								value={employeeCodeFilter}
							/>
						</div>
					</th>,
				);
			} else {
				// Format header names - replace underscores with spaces
				const formattedKey = key.replace(/_/g, ' ');
				headers.push(
					<th key={key} className='bg-light'>
						{formattedKey}
					</th>,
				);
			}
		});

		// Add Actions header
		headers.push(
			<th key='actions' className='bg-light text-center'>
				Actions
			</th>,
		);

		return headers;
	};

	const renderTableRows = () => {
		if (isLoading) {
			return (
				<tr>
					<td
						colSpan={filteredData[0] ? Object.keys(filteredData[0]).length + 2 : 1}
						className='text-center'>
						<TableSpinner />
					</td>
				</tr>
			);
		}

		if (filteredData.length === 0) {
			return (
				<tr>
					<td
						colSpan={reportData[0] ? Object.keys(reportData[0]).length + 2 : 1}
						className='text-center'>
						<div className='d-flex justify-content-center align-items-center py-5'>
							<div className='text-muted'>{getNoRecordsMessage()}</div>
						</div>
					</td>
				</tr>
			);
		}

		return filteredData.map((row, index) => {
			const status = row.Status || row.status;
			const rowClassName = getStatusColor(status);
			const isApproved = status?.toLowerCase() === 'approved';
			const isRejected = status?.toLowerCase() === 'rejected';

			return (
				<tr key={index} className={rowClassName}>
					<td className='fw-bold'>{index + 1}</td>
					{Object.keys(row).map((key, i) => {
						// Skip medicalAllowanceID from display
						if (key === 'medicalAllowanceID') return null;
						
						const value = row[key];
						// Format currency for Allowance_Amount
						if (key === 'Allowance_Amount') {
							return (
								<td key={i} className='fw-bold text-end'>
									₹ {parseFloat(value || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
								</td>
							);
						}
						return (
							<td key={i} className='fw-bold'>
								{value || '-'}
							</td>
						);
					})}
					<td className='text-center'>
						<div className='d-flex gap-2 justify-content-center'>
							<Button
								color='success'
								isLight
								className='px-3 py-2'
								onClick={() => handleApprove(row)}
								isDisable={isApproved || isRejected}
								title='Approve Allowance'>
								<Icon icon='CheckCircle' size='lg' />
							</Button>
							<Button
								color='danger'
								isLight
								className='px-3 py-2'
								onClick={() => handleReject(row)}
								isDisable={isApproved || isRejected}
								title='Reject Allowance'>
								<Icon icon='Cancel' size='lg' />
							</Button>
						</div>
					</td>
				</tr>
			);
		});
	};

	const renderStatusDropdown = () => (
		<FormGroup id='statusFilter' label='Status' isFloating className='mb-3'>
			<Select
				ariaLabel='Status'
				className='form-select'
				onChange={handleStatusChange}
				value={selectedStatus}
				isTouched={true}
				isValid={true}>
				{statusOptions.map((status) => (
					<option key={status} value={status} className='text-capitalize'>
						{status === 'all' ? 'All Statuses' : status}
					</option>
				))}
			</Select>
		</FormGroup>
	);

	const getNoRecordsMessage = () => {
		if (selectedStatus === 'all') {
			return 'No records found for the selected date range';
		}
		return `No records found for the selected filters`;
	};

	const handleApprove = (item: any) => {
		Swal.fire({
			title: 'Confirm Approval',
			text: `Are you sure you want to approve medical allowance for ${item.Employee_Name}?`,
			icon: 'question',
			showCancelButton: true,
			confirmButtonColor: '#3085d6',
			cancelButtonColor: '#d33',
			confirmButtonText: 'Yes, approve!',
			cancelButtonText: 'Cancel'
		}).then((result) => {
			if (result.isConfirmed) {
				const payload = {
					medicalAllowanceID: item.medicalAllowanceID,
					action: 'approve'
				};
				dispatch(updateMedicalAllowanceStatusStart(payload));
			}
		});
	};

	const handleReject = (item: any) => {
		Swal.fire({
			title: 'Confirm Rejection',
			text: `Are you sure you want to reject medical allowance for ${item.Employee_Name}?`,
			icon: 'warning',
			showCancelButton: true,
			confirmButtonColor: '#d33',
			cancelButtonColor: '#3085d6',
			confirmButtonText: 'Yes, reject!',
			cancelButtonText: 'Cancel'
		}).then((result) => {
			if (result.isConfirmed) {
				const payload = {
					medicalAllowanceID: item.medicalAllowanceID,
					action: 'reject'
				};
				dispatch(updateMedicalAllowanceStatusStart(payload));
			}
		});
	};

	return (
		<PageWrapper>
			<SubHeader className='mt-3'>
				<SubHeaderLeft>
					<Search />
				</SubHeaderLeft>
			</SubHeader>
			<SubHeader className='mt-3'>
				<SubHeaderLeft>
					<CardLabel icon='LocalHospital' iconColor='info'>
						<CardTitle>Medical Allowance Report</CardTitle>
					</CardLabel>
				</SubHeaderLeft>
			</SubHeader>
			<Page container='fluid'>
				<Card stretch>
					<div className='row h-100 pb-3'>
						<div className='col-lg-12 col-md-12'>
							<div className='row ps-4 pt-4'>
								<div className='col-12'>
									<div className='row'>
										<div className='col-md-4'>
											<FormGroup
												id='startDate'
												label='Start Date'
												isFloating
												className='mb-3'>
												<Input
													type='date'
													onBlur={formik.handleBlur}
													onChange={formik.handleChange}
													value={formik.values.startDate}
													name='startDate'
													isValid={
														formik.touched.startDate &&
														!formik.errors.startDate
													}
													isTouched={formik.touched.startDate}
													invalidFeedback={formik.errors.startDate}
												/>
											</FormGroup>
										</div>
										<div className='col-md-4'>
											<FormGroup
												id='endDate'
												label='End Date'
												isFloating
												className='mb-3'>
												<Input
													type='date'
													onBlur={formik.handleBlur}
													onChange={formik.handleChange}
													value={formik.values.endDate}
													name='endDate'
													isValid={
														formik.touched.endDate &&
														!formik.errors.endDate
													}
													isTouched={formik.touched.endDate}
													invalidFeedback={formik.errors.endDate}
												/>
											</FormGroup>
										</div>
										<div className='col-md-4'>{renderStatusDropdown()}</div>
									</div>
								</div>
							</div>

							<div className='row mt-3'>
								<div className='col-12' style={{ textAlign: 'right' }}>
									<Button
										className='me-3'
										color='primary'
										icon={!isLoading ? 'search' : ''}
										onClick={() => {
											if (process.env.NODE_ENV === 'development') console.log('Generating report...');
											formik.handleSubmit();
										}}
										isDisable={
											(!formik.isValid && !!formik.submitCount) || isLoading
										}>
										Generate Report
									</Button>
								</div>
							</div>

							<CardBody>
								<div
									className='table-responsive'
									style={{ maxHeight: '500px', overflowY: 'auto' }}>
									{isLoading ? (
										<div className='text-center py-5'>
											<TableSpinner />
										</div>
									) : (
										<>
											{reportData.length > 0 ? (
												<>
													<table
														className='table table-modern table-hover'
														style={tableStyles}>
														<thead
															style={{
																position: 'sticky',
																top: 0,
																backgroundColor: 'white',
																zIndex: 1,
															}}>
															<tr>{renderTableHeaders()}</tr>
														</thead>
														<tbody>{renderTableRows()}</tbody>
													</table>
													{filteredData.length > 0 && (
														<div className='row mt-3'>
															<div
																className='col-12'
																style={{ textAlign: 'right' }}>
																<Button
																	className='me-3'
																	color='success'
																	icon='download'
																	onClick={handleExport}>
																	Export Report
																</Button>
															</div>
														</div>
													)}
												</>
											) : (
												<div className='text-center py-5'>
													<div className='text-muted h5'>
														No data available. Please select a date
														range and click Generate Report.
													</div>
												</div>
											)}
										</>
									)}
								</div>
							</CardBody>
						</div>
					</div>
				</Card>
			</Page>
		</PageWrapper>
	);
};

export default MedicalAllowanceReportPage;

